import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { 
  CreditCard, 
  Search,
  MoreVertical,
  Loader2,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  RefreshCw,
  Ban,
  Play,
  Building2,
  Filter,
  TrendingUp,
  Sparkles
} from "lucide-react";
import { Input } from "../../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPlan, setFilterPlan] = useState("all");
  const [selectedSub, setSelectedSub] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogAction, setDialogAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subsResponse, orgsResponse] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/admin/organizations`)
      ]);
      setSubscriptions(subsResponse.data);
      setOrganizations(orgsResponse.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || "Organisation inconnue";
  };

  const handleAction = (subscription, action) => {
    setSelectedSub(subscription);
    setDialogAction(action);
    setDialogOpen(true);
  };

  const executeAction = async () => {
    if (!selectedSub || !dialogAction) return;
    
    setActionLoading(true);
    try {
      let endpoint = "";
      let method = "put";
      
      switch (dialogAction) {
        case "cancel":
          endpoint = `/admin/subscriptions/${selectedSub.id}/cancel`;
          break;
        case "activate":
          endpoint = `/admin/subscriptions/${selectedSub.id}/activate`;
          break;
        case "extend_trial":
          endpoint = `/admin/subscriptions/${selectedSub.id}/extend-trial`;
          break;
        case "upgrade":
          endpoint = `/admin/subscriptions/${selectedSub.id}/upgrade`;
          break;
        case "downgrade":
          endpoint = `/admin/subscriptions/${selectedSub.id}/downgrade`;
          break;
        default:
          return;
      }
      
      await axios[method](`${API}${endpoint}`);
      toast.success("Action effectuée avec succès");
      fetchData();
    } catch (error) {
      console.error("Error executing action:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'action");
    } finally {
      setActionLoading(false);
      setDialogOpen(false);
      setSelectedSub(null);
      setDialogAction(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (subscription) => {
    const status = subscription.status;
    const isTrialExpiring = subscription.status === "trial" && 
      subscription.trial_ends_at && 
      new Date(subscription.trial_ends_at) < new Date(Date.now() + 2 * 24 * 60 * 60 * 1000);
    
    if (status === "active") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium">
          <CheckCircle className="w-3 h-3" />
          Actif
        </span>
      );
    } else if (status === "trial") {
      return (
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          isTrialExpiring ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
        }`}>
          <Clock className="w-3 h-3" />
          Essai {isTrialExpiring && "(expire bientôt)"}
        </span>
      );
    } else if (status === "canceled" || status === "cancelled") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
          <Ban className="w-3 h-3" />
          Annulé
        </span>
      );
    } else if (status === "expired") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <XCircle className="w-3 h-3" />
          Expiré
        </span>
      );
    } else if (status === "payment_failed") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
          <AlertTriangle className="w-3 h-3" />
          Paiement échoué
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
        {status}
      </span>
    );
  };

  const getPlanBadge = (plan) => {
    if (plan === "premium") {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium">
          <Sparkles className="w-3 h-3" />
          Premium
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
        <CreditCard className="w-3 h-3" />
        Essentiel
      </span>
    );
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    const orgName = getOrgName(sub.organization_id).toLowerCase();
    const matchesSearch = orgName.includes(searchTerm.toLowerCase()) || 
      sub.organization_id.includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || sub.status === filterStatus;
    const matchesPlan = filterPlan === "all" || sub.plan === filterPlan;
    return matchesSearch && matchesStatus && matchesPlan;
  });

  // Stats
  const stats = {
    total: subscriptions.length,
    active: subscriptions.filter(s => s.status === "active").length,
    trial: subscriptions.filter(s => s.status === "trial").length,
    canceled: subscriptions.filter(s => s.status === "canceled" || s.status === "cancelled").length,
    essential: subscriptions.filter(s => s.plan === "essential" && (s.status === "active" || s.status === "trial")).length,
    premium: subscriptions.filter(s => s.plan === "premium" && (s.status === "active" || s.status === "trial")).length,
  };

  const dialogMessages = {
    cancel: {
      title: "Annuler l'abonnement",
      description: `Êtes-vous sûr de vouloir annuler l'abonnement de "${getOrgName(selectedSub?.organization_id)}" ?`,
      confirmText: "Annuler l'abonnement",
      confirmClass: "bg-red-600 hover:bg-red-700"
    },
    activate: {
      title: "Activer l'abonnement",
      description: `Voulez-vous activer l'abonnement de "${getOrgName(selectedSub?.organization_id)}" ?`,
      confirmText: "Activer",
      confirmClass: "bg-emerald-600 hover:bg-emerald-700"
    },
    extend_trial: {
      title: "Prolonger l'essai",
      description: `Prolonger l'essai de "${getOrgName(selectedSub?.organization_id)}" de 7 jours supplémentaires ?`,
      confirmText: "Prolonger",
      confirmClass: "bg-blue-600 hover:bg-blue-700"
    },
    upgrade: {
      title: "Passer en Premium",
      description: `Passer "${getOrgName(selectedSub?.organization_id)}" au plan Premium (69€/mois) ?`,
      confirmText: "Passer en Premium",
      confirmClass: "bg-violet-600 hover:bg-violet-700"
    },
    downgrade: {
      title: "Rétrograder en Essentiel",
      description: `Rétrograder "${getOrgName(selectedSub?.organization_id)}" au plan Essentiel (49€/mois) ?`,
      confirmText: "Rétrograder",
      confirmClass: "bg-slate-600 hover:bg-slate-700"
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-subscriptions-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">
            Abonnements
          </h1>
          <p className="text-slate-500 mt-1">
            Gérez les abonnements et périodes d'essai
          </p>
        </div>
        
        <Button 
          variant="outline" 
          onClick={fetchData}
          className="rounded-xl"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl p-4 border border-slate-100">
          <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-100">
          <p className="text-2xl font-bold text-emerald-600">{stats.active}</p>
          <p className="text-xs text-emerald-700">Actifs</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-2xl font-bold text-blue-600">{stats.trial}</p>
          <p className="text-xs text-blue-700">En essai</p>
        </div>
        <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
          <p className="text-2xl font-bold text-slate-600">{stats.canceled}</p>
          <p className="text-xs text-slate-500">Annulés</p>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
          <p className="text-2xl font-bold text-blue-600">{stats.essential}</p>
          <p className="text-xs text-blue-700">Essentiel</p>
        </div>
        <div className="bg-violet-50 rounded-xl p-4 border border-violet-100">
          <p className="text-2xl font-bold text-violet-600">{stats.premium}</p>
          <p className="text-xs text-violet-700">Premium</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            type="text"
            placeholder="Rechercher par établissement..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 h-10 rounded-xl"
            data-testid="search-subscriptions"
          />
        </div>
        
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[180px] rounded-xl">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            <SelectItem value="active">Actif</SelectItem>
            <SelectItem value="trial">En essai</SelectItem>
            <SelectItem value="canceled">Annulé</SelectItem>
            <SelectItem value="expired">Expiré</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={filterPlan} onValueChange={setFilterPlan}>
          <SelectTrigger className="w-[180px] rounded-xl">
            <CreditCard className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les plans</SelectItem>
            <SelectItem value="essential">Essentiel</SelectItem>
            <SelectItem value="premium">Premium</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Subscriptions List */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        {/* Table Header */}
        <div className="grid grid-cols-12 gap-4 p-4 bg-slate-50 border-b border-slate-100 text-sm font-medium text-slate-500">
          <div className="col-span-4">Établissement</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-2 hidden md:block">Fin essai/période</div>
          <div className="col-span-1 hidden md:block">Prix</div>
          <div className="col-span-1"></div>
        </div>

        {/* Table Body */}
        {filteredSubscriptions.length > 0 ? (
          <div className="divide-y divide-slate-100">
            {filteredSubscriptions.map((sub) => (
              <div 
                key={sub.id} 
                className="grid grid-cols-12 gap-4 p-4 items-center hover:bg-slate-50 transition-colors"
                data-testid={`subscription-row-${sub.id}`}
              >
                <div className="col-span-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">
                      {getOrgName(sub.organization_id)}
                    </p>
                    <p className="text-xs text-slate-500 truncate">
                      Depuis {formatDate(sub.created_at)}
                    </p>
                  </div>
                </div>
                <div className="col-span-2">
                  {getPlanBadge(sub.plan)}
                </div>
                <div className="col-span-2">
                  {getStatusBadge(sub)}
                </div>
                <div className="col-span-2 hidden md:block text-sm text-slate-600">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-slate-400" />
                    {sub.status === "trial" 
                      ? formatDate(sub.trial_ends_at)
                      : formatDate(sub.current_period_end)
                    }
                  </div>
                </div>
                <div className="col-span-1 hidden md:block font-medium text-slate-900">
                  {sub.price}€
                </div>
                <div className="col-span-1 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {sub.status === "trial" && (
                        <>
                          <DropdownMenuItem onClick={() => handleAction(sub, "extend_trial")}>
                            <Clock className="w-4 h-4 mr-2" />
                            Prolonger l'essai (+7j)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleAction(sub, "activate")}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Activer l'abonnement
                          </DropdownMenuItem>
                        </>
                      )}
                      {(sub.status === "active" || sub.status === "trial") && (
                        <>
                          <DropdownMenuSeparator />
                          {sub.plan === "essential" ? (
                            <DropdownMenuItem onClick={() => handleAction(sub, "upgrade")}>
                              <TrendingUp className="w-4 h-4 mr-2" />
                              Passer en Premium
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => handleAction(sub, "downgrade")}>
                              <CreditCard className="w-4 h-4 mr-2" />
                              Rétrograder en Essentiel
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleAction(sub, "cancel")}
                            className="text-red-600"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Annuler l'abonnement
                          </DropdownMenuItem>
                        </>
                      )}
                      {(sub.status === "canceled" || sub.status === "cancelled" || sub.status === "expired") && (
                        <DropdownMenuItem onClick={() => handleAction(sub, "activate")}>
                          <Play className="w-4 h-4 mr-2" />
                          Réactiver
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-12 text-center">
            <CreditCard className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucun abonnement trouvé</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {dialogAction && dialogMessages[dialogAction]?.title}
            </DialogTitle>
            <DialogDescription>
              {dialogAction && dialogMessages[dialogAction]?.description}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={actionLoading}
            >
              Annuler
            </Button>
            <Button
              onClick={executeAction}
              disabled={actionLoading}
              className={dialogAction && dialogMessages[dialogAction]?.confirmClass}
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {dialogAction && dialogMessages[dialogAction]?.confirmText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSubscriptions;
